import { FC, useEffect, useMemo } from 'react'

import { injectJsonLd, MetaData, setPageMetadata } from '@shared/lib/seo/seo-utils'

type JsonLdInput = string | Record<string, unknown>;

interface SEOProps extends MetaData {
  jsonLd?: Array<{ id: string; data: JsonLdInput }>;
}

/**
 * Компонент для управления SEO метаданными на странице
 * Использование:
 * <SEO metadata={{
 *   title: 'Заголовок страницы',
 *   description: 'Описание страницы',
 *   keywords: 'ключевые, слова',
 *   ogTitle: 'Заголовок для социальных сетей',
 *   ogDescription: 'Описание для социальных сетей',
 *   ogImage: '/path/to/image.jpg',
 *   ogUrl: 'https://hive-software.com/page',
 *   canonicalUrl: 'https://hive-software.com/page'
 * }} />
 */
export const SEO: FC<SEOProps> = ({ jsonLd, ...meta }) => {
  const metaString = useMemo(() => JSON.stringify(meta), [ meta ])
  const jsonLdString = useMemo(() => JSON.stringify(jsonLd ?? []), [ jsonLd ])

  useEffect(() => {
    setPageMetadata(meta)

    // Inject JSON-LD blocks
    if (jsonLd && jsonLd.length) {
      jsonLd.forEach(({ id, data }) => {
        const payload = typeof data === 'string' ? data : JSON.stringify(data)
        injectJsonLd(payload, id)
      })
    }

    // Clean up injected JSON-LD on unmount to prevent duplicates on SPA navigation
    return () => {
      if (jsonLd && jsonLd.length) {
        jsonLd.forEach(({ id }) => {
          const el = document.getElementById(id)
          if (el) el.remove()
        })
      }
    }
  }, [ meta, jsonLd, metaString, jsonLdString ])

  // Компонент не рендерит никакой UI
  return null
}

export default SEO
